import { Box, Stack, Tag, TagLabel } from '@chakra-ui/react';
import { TSAirport } from '@pelicargo/types';
import { kmToMi } from '@pelicargo/utils';

import { trpc, TrpcOutputs } from '../../config/trpc';
import { AirportInput, AirportInputProps } from './AirportInput';

type Props = AirportInputProps & {
  currentAirportId: string;
  setValue: (name: string, value: any) => void;
};

/**
 * `getAirportsByDistance` returns raw Typesense hits, and the Typesense client types both halves of a
 * hit too loosely to use: `document` is widened to `object`, and `geo_distance_meters` is hard-coded to
 * `{ location: number }` even though Typesense keys it by the geopoint field that was sorted on
 * (`airport_latlng` here). Intersect the inferred hit with the real shapes rather than redeclaring it.
 */
type NearbyAirportHit = NonNullable<TrpcOutputs['getAirportsByDistance']>[number] & {
  document: TSAirport;
  geo_distance_meters: Record<'airport_latlng', number>;
};

// Wrapper on AirportInput that shows nearby airports as tags
export const NearbyAirportInput = ({ currentAirportId, setValue, ...props }: Props) => {
  const { data: nearbyAirports } = trpc.getAirportsByDistance.useQuery(
    { id: currentAirportId },
    { enabled: !!currentAirportId },
  );

  return (
    <Box gridColumn="span 1">
      <AirportInput tabIndex={0} isRequired {...props} />
      <Stack direction="row" spacing={2} mt={2} flexWrap="wrap">
        {nearbyAirports?.map(({ document, geo_distance_meters }: NearbyAirportHit) => (
          <Tag
            key={document.id}
            size="sm"
            borderRadius="full"
            variant="outline"
            borderColor="offBlack"
            color="offBlack"
            fontWeight={document.airport_main_gateway ? 'bold' : 'normal'}
            cursor="pointer"
            onClick={() => setValue(props.name, document.id)}
            _hover={{ opacity: 0.8 }}
          >
            <TagLabel whiteSpace="nowrap">
              {`${document.id} (${kmToMi(geo_distance_meters.airport_latlng / 1000).toFixed(0)} mi)`}
            </TagLabel>
          </Tag>
        ))}
      </Stack>
    </Box>
  );
};
